import { useEffect, useRef, useState } from "react";
import { BadgeCheckIcon, PaletteIcon, ShieldIcon } from "lucide-react";

import {
  useMyLicense,
  useBranding,
  usePutBranding,
  useSuperUpsertLicense,
} from "@/api/licensing";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/store/auth";
import { useTenantStore } from "@/store/tenant";

const NAVY = "#263238";
const ACCENT = "#ff6d00";
const PLANS = ["basic", "compliance", "command", "ai", "banking", "onprem"];

function pct(used: number, limit?: number | null): number | null {
  if (limit === null || limit === undefined || limit < 0) return null; // unlimited
  if (limit === 0) return 100;
  return Math.min(100, Math.round((used / limit) * 100));
}

function UsageBar({
  label,
  used,
  limit,
  unit = "",
}: {
  label: string;
  used: number;
  limit?: number | null;
  unit?: string;
}) {
  const p = pct(used, limit);
  const over80 = p !== null && p >= 80;
  const limitText = limit === null || limit === undefined || limit < 0 ? "∞" : `${limit}${unit}`;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="font-medium" style={{ color: NAVY }}>{label}</span>
        <span className="text-muted-foreground">
          {used}{unit} / {limitText}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-[#e2e6ea]">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${p ?? 4}%`,
            background: over80 ? ACCENT : NAVY,
          }}
        />
      </div>
    </div>
  );
}

function ExpiryBadge({ expiresAt, state }: { expiresAt?: string | null; state: string }) {
  if (!expiresAt) {
    return <span className="text-xs text-muted-foreground">Perpetual license</span>;
  }
  const days = Math.ceil(
    (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
  );
  const color = state === "expired" ? "#b00020" : state === "grace" ? ACCENT : NAVY;
  return (
    <span className="text-xs font-medium" style={{ color }}>
      {state === "expired"
        ? "Expired — read-only"
        : days >= 0
          ? `Expires in ${days} day(s)`
          : `Lapsed ${Math.abs(days)} day(s) ago (grace)`}
    </span>
  );
}

function WhiteLabelSection() {
  const { data } = useBranding();
  const put = usePutBranding();
  const [logo, setLogo] = useState("");
  const [primary, setPrimary] = useState(NAVY);
  const [accent, setAccent] = useState(ACCENT);
  const [domain, setDomain] = useState("");
  // A29 — seed the form from branding EXACTLY ONCE when it first loads, in an effect
  // (not during render). Fixes: (a) the old guard only seeded when logo_url was truthy,
  // so a tenant with colors/domain but no logo never seeded; (b) the old guard let a
  // refetch re-run the seed and clobber the user's unsaved edits.
  const seeded = useRef(false);
  useEffect(() => {
    if (data && !seeded.current) {
      seeded.current = true;
      setLogo(data.logo_url || "");
      setPrimary(data.primary_color);
      setAccent(data.accent_color);
      setDomain(data.custom_domain || "");
    }
  }, [data]);
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h3 className="flex items-center gap-2 font-semibold" style={{ color: NAVY }}>
          <PaletteIcon className="h-4 w-4" /> White-label
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Logo URL</Label>
            <Input value={logo} onChange={(e) => setLogo(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Custom domain</Label>
            <Input value={domain} onChange={(e) => setDomain(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Primary color</Label>
            <Input value={primary} onChange={(e) => setPrimary(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Accent color</Label>
            <Input value={accent} onChange={(e) => setAccent(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            disabled={put.isPending}
            onClick={() =>
              put.mutate({
                logo_url: logo || null,
                primary_color: primary,
                accent_color: accent,
                custom_domain: domain || null,
              })
            }
          >
            Save branding
          </Button>
        </div>
        {put.isError && (
          <UpgradePrompt
            compact
            feature="white_label"
            message="White-labeling requires the Banking plan or higher"
          />
        )}
      </CardContent>
    </Card>
  );
}

function SuperAdminEditor() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const upsert = useSuperUpsertLicense();
  const [plan, setPlan] = useState("basic");
  const [cameraLimit, setCameraLimit] = useState(16);
  const [userLimit, setUserLimit] = useState(5);
  const [isTrial, setIsTrial] = useState(false);
  const [expires, setExpires] = useState("");
  if (!tenantId) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          Scope to a tenant (top-right switcher) to edit its license.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h3 className="flex items-center gap-2 font-semibold" style={{ color: NAVY }}>
          <ShieldIcon className="h-4 w-4" /> Super-admin · License editor
        </h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <Label>Plan</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
            >
              {PLANS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Cameras</Label>
            <Input type="number" value={cameraLimit}
              onChange={(e) => setCameraLimit(Number(e.target.value))} />
          </div>
          <div className="space-y-1">
            <Label>Users</Label>
            <Input type="number" value={userLimit}
              onChange={(e) => setUserLimit(Number(e.target.value))} />
          </div>
          <div className="space-y-1">
            <Label>Expires</Label>
            <Input type="date" value={expires}
              onChange={(e) => setExpires(e.target.value)} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isTrial}
            onChange={(e) => setIsTrial(e.target.checked)} />
          Trial license
        </label>
        <div className="flex justify-end">
          <Button
            disabled={upsert.isPending}
            onClick={() =>
              upsert.mutate({
                tenantId,
                body: {
                  plan,
                  camera_limit: cameraLimit,
                  user_limit: userLimit,
                  is_trial: isTrial,
                  expires_at: expires ? new Date(expires).toISOString() : null,
                },
              })
            }
          >
            Save license
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function LicensingPage() {
  const { data, isLoading } = useMyLicense();
  const role = useAuthStore((s) => s.user?.role);
  const isSuperAdmin = useTenantStore((s) => s.isSuperAdmin);

  if (isLoading) return <p className="text-sm">Loading…</p>;
  if (!data) return <p className="text-sm">No license information.</p>;

  const { limits, usage } = data;
  const nearLimit =
    (pct(usage.cameras_used, limits.camera_limit) ?? 0) >= 80 ||
    (pct(usage.users_used, limits.user_limit) ?? 0) >= 80 ||
    (pct(usage.storage_used_gb, limits.storage_limit_gb) ?? 0) >= 80;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight"
          style={{ color: NAVY }}>
          <BadgeCheckIcon className="h-6 w-6" />
          Licensing &amp; Usage
        </h1>
        <div className="mt-1 flex items-center gap-3">
          <span
            className="rounded-full px-3 py-0.5 text-sm font-semibold text-white"
            style={{ background: NAVY }}
          >
            {limits.plan ? limits.plan.toUpperCase() : "UNLICENSED"}
            {limits.is_trial ? " · TRIAL" : ""}
          </span>
          <ExpiryBadge expiresAt={limits.expires_at} state={data.lifecycle_state} />
        </div>
      </div>

      {nearLimit && (
        <UpgradePrompt
          message="You're approaching a plan limit"
          currentPlan={limits.plan}
          upgradeHint="Upgrade to add capacity before you hit a hard limit."
        />
      )}

      <Card>
        <CardContent className="space-y-4 p-5">
          <UsageBar label="Cameras" used={usage.cameras_used} limit={limits.camera_limit} />
          <UsageBar label="Users" used={usage.users_used} limit={limits.user_limit} />
          <UsageBar label="Storage" used={Math.round(usage.storage_used_gb)}
            limit={limits.storage_limit_gb} unit="GB" />
          <UsageBar label="AI events (month)" used={usage.ai_events_month}
            limit={limits.ai_event_quota_month} />
          <div className="text-xs text-muted-foreground">
            Retention cap: {limits.retention_max_days ?? "∞"} days · API rate:{" "}
            {limits.api_rate_limit_per_min ?? "∞"}/min · Export:{" "}
            {limits.export_enabled ? "enabled" : "disabled"} · Video wall:{" "}
            {limits.video_wall_enabled ? "enabled" : "disabled"}
          </div>
        </CardContent>
      </Card>

      {role === "admin" && <WhiteLabelSection />}
      {isSuperAdmin && <SuperAdminEditor />}
    </div>
  );
}

export default LicensingPage;
